/**
 * Beauty Effect Engine Core
 * Platform-agnostic engine implementation with dependency injection
 */

import { EngineConfig } from './config.js';
import { FacebetterError, checkResult } from './errors.js';
import { ProcessMode, BackgroundMode } from './constants.js';

/**
 * Beauty effect engine class
 * Provides high-level API for face beauty effects processing
 * Uses dependency injection for platform-specific functionality
 */
export class BeautyEffectEngine {
  /**
   * Creates a new BeautyEffectEngine instance
   * @param {EngineConfig|Object} config - EngineConfig object or configuration object
   * @param {Object} platformAPI - Platform-specific API (injected)
   * @param {Function} platformAPI.loadWasmModule - WASM module loader
   * @param {Function} platformAPI.getWasmModule - Get WASM module instance
   * @param {Function} platformAPI.getWasmBuffer - Get WASM memory buffer
   * @param {Function} platformAPI.toImageData - Convert to ImageData
   * @param {Function} platformAPI.verifyAppKeyOnline - Verify app key online
   * @param {Function} [platformAPI.ensureGPUPixelCanvas] - Ensure GPU canvas exists (browser only)
   * @param {Function} [platformAPI.cleanupGPUPixelCanvas] - Cleanup GPU canvas (browser only)
   */
  constructor(config, platformAPI) {
    if (!config) {
      throw new FacebetterError('Config is required. Expected EngineConfig or configuration object');
    }

    if (!platformAPI) {
      throw new FacebetterError('Platform API is required');
    }

    // 如果传入的是 EngineConfig 实例，直接使用；否则创建新的 EngineConfig
    let engineConfig;
    if (config instanceof EngineConfig) {
      engineConfig = config;
    } else if (config && typeof config === 'object') {
      engineConfig = new EngineConfig(config);
    } else {
      throw new FacebetterError('Invalid configuration. Expected EngineConfig or configuration object');
    }

    if (!engineConfig.isValid()) {
      throw new FacebetterError('Invalid config: ' + engineConfig.toString());
    }

    this.config = engineConfig;
    this.appId = engineConfig.appId;
    this.appKey = engineConfig.appKey;
    this.licenseJson = engineConfig.licenseJson;
    this.resourcePath = '/facebetter/resource.bundle';
    this.enginePtr = null;
    this.initialized = false;
    this.srcBufferPtr = null;
    this.dstBufferPtr = null;
    this.bufferSize = 0;
    
    // Store platform API
    this._platformAPI = platformAPI;
    this._loadWasmModule = platformAPI.loadWasmModule;
    this._getWasmModule = platformAPI.getWasmModule;
    this._getWasmBuffer = platformAPI.getWasmBuffer;
    this._toImageData = platformAPI.toImageData;
    this._verifyAppKeyOnline = platformAPI.verifyAppKeyOnline;
    this._ensureGPUPixelCanvas = platformAPI.ensureGPUPixelCanvas;
    this._cleanupGPUPixelCanvas = platformAPI.cleanupGPUPixelCanvas;
    
    // Browser-specific state
    this._gpupixelCanvas = null;
    this._createdGPUPixelCanvas = false;
    this._offscreenCanvas = null;
    this._offscreenCtx = null;
    
    // Automatically create gpupixel_canvas if it doesn't exist (browser only)
    if (this._ensureGPUPixelCanvas) {
      this._ensureGPUPixelCanvas();
    }
    
    // Start loading WASM module immediately in constructor
    this._wasmLoadPromise = this._loadWasmModule();
    this._initPromise = null;
  }

  /**
   * Creates a timeout promise
   * @private
   * @param {number} timeout - Timeout in milliseconds
   * @param {string} operation - Operation name for error message
   * @returns {Promise} Promise that rejects after timeout
   */
  _createTimeout(timeout, operation) {
    return new Promise((_, reject) => {
      setTimeout(() => {
        reject(new FacebetterError(
          `${operation} timed out after ${timeout}ms. Please check your network connection or try increasing the timeout.`,
          'TIMEOUT'
        ));
      }, timeout);
    });
  }

  /**
   * Enhances error with context information
   * @private
   * @param {Error} error - Original error
   * @param {string} context - Context information
   * @returns {FacebetterError} Enhanced error
   */
  _enhanceError(error, context) {
    if (error instanceof FacebetterError) {
      return error;
    }

    let errorCode = 'UNKNOWN_ERROR';
    let message = error.message || 'Unknown error occurred';

    // Categorize errors
    if (error.message?.includes('timeout') || error.message?.includes('TIMEOUT')) {
      errorCode = 'TIMEOUT';
    } else if (error.message?.includes('network') || error.message?.includes('fetch')) {
      errorCode = 'NETWORK_ERROR';
    } else if (error.message?.includes('WASM') || error.message?.includes('wasm')) {
      errorCode = 'WASM_LOAD_ERROR';
    } else if (error.message?.includes('license') || error.message?.includes('auth')) {
      errorCode = 'LICENSE_ERROR';
    }

    return new FacebetterError(
      context ? `${context}: ${message}` : message,
      errorCode
    );
  }

  /**
   * Initializes the engine
   * @param {Object} [options] - Initialization options
   * @param {number} [options.timeout] - Timeout in milliseconds (default: 30000 for WASM, 10000 for auth)
   * @param {number} [options.authTimeout] - Timeout for online authentication in milliseconds (default: 10000)
   * @returns {Promise<void>} Promise that resolves when initialization is complete
   */
  async init(options = {}) {
    // 并发控制：如果已经初始化，直接返回
    if (this.initialized) {
      return;
    }

    // 并发控制：如果正在初始化，返回同一个 Promise
    if (this._initPromise) {
      return this._initPromise;
    }

    // 创建初始化 Promise
    this._initPromise = (async () => {
      try {
        const wasmTimeout = options.timeout || 30000;
        const authTimeout = options.authTimeout || 10000;

        // 等待 WASM 模块加载（带超时）
        try {
          await Promise.race([
            this._wasmLoadPromise,
            this._createTimeout(wasmTimeout, 'WASM module loading')
          ]);
        } catch (error) {
          throw this._enhanceError(error, 'Failed to load WASM module');
        }

        const Module = this._getWasmModule();

        let licenseJsonToUse = this.licenseJson;

        // 如果用户提供了 appId 和 appKey（但没有提供 licenseJson），则在 JS 层发送 HTTP 请求
        if (this.appId && this.appKey && !this.licenseJson && this._verifyAppKeyOnline) {
          try {
            // 在线认证（带超时）
            const authResponse = await Promise.race([
              this._verifyAppKeyOnline(this.appId, this.appKey),
              this._createTimeout(authTimeout, 'Online authentication')
            ]);
            
            if (!authResponse) {
              throw new FacebetterError(
                'Failed to get server response from online_auth API. The server returned an empty response.',
                'AUTH_EMPTY_RESPONSE'
              );
            }

            // 将服务器响应转换为 JSON 字符串，作为 licenseJson 传递给 WASM 层
            licenseJsonToUse = JSON.stringify(authResponse);
            console.log('Online auth response received, using as licenseJson');
          } catch (error) {
            if (error instanceof FacebetterError && error.code === 'TIMEOUT') {
              throw new FacebetterError(
                `Online authentication timed out after ${authTimeout}ms. Please check your network connection or provide licenseJson directly.`,
                'AUTH_TIMEOUT'
              );
            }
            throw this._enhanceError(error, 'Failed to verify app key online');
          }
        }

        // 如果用户直接提供了 licenseJson，则直接使用
        // 如果通过 appId/appKey 获取到了响应，则使用响应作为 licenseJson
        // WASM 层只需要验证 licenseJson，不需要发送 HTTP 请求
        const enginePtr = Module.ccall(
          'CreateBeautyEffectEngine',
          'number',
          ['string', 'string'],
          [
            this.resourcePath,
            licenseJsonToUse || ''  // 使用 licenseJson 验证
          ]
        );

        if (!enginePtr) {
          throw new FacebetterError(
            'Failed to create BeautyEffect engine. This may be due to invalid license or resource path issues.',
            'ENGINE_CREATE_FAILED'
          );
        }

        this.enginePtr = enginePtr;
        this.initialized = true;
        this._initPromise = null; // 清除 Promise 缓存，允许重新初始化（如果需要）
      } catch (error) {
        this._initPromise = null; // 清除 Promise 缓存，允许重试
        throw error;
      }
    })();

    return this._initPromise;
  }

  /**
   * Destroys the engine and releases resources
   */
  destroy() {
    if (!this.initialized || !this.enginePtr) {
      return;
    }

    const Module = this._getWasmModule();

    if (this.srcBufferPtr) {
      Module._free(this.srcBufferPtr);
      this.srcBufferPtr = null;
    }

    if (this.dstBufferPtr) {
      Module._free(this.dstBufferPtr);
      this.dstBufferPtr = null;
    }

    Module.ccall('DestroyBeautyEffectEngine', null, ['number'], [this.enginePtr]);
    this.enginePtr = null;
    this.initialized = false;
    this.bufferSize = 0;
    this._initPromise = null; // 清除初始化 Promise
    
    // Clean up offscreen canvas
    if (this._offscreenCanvas) {
      this._offscreenCanvas = null;
      this._offscreenCtx = null;
    }
    
    // Clean up gpupixel_canvas if we created it
    if (this._cleanupGPUPixelCanvas) {
      this._cleanupGPUPixelCanvas();
    }
  }

  /**
   * Sets log configuration
   * Can be called before init() since SetLogConfig is a global function
   * @param {Object} config - Log configuration
   * @param {boolean} config.consoleEnabled - Enable console logging
   * @param {boolean} config.fileEnabled - Enable file logging
   * @param {number} config.level - Log level
   * @param {string} config.fileName - Log file name
   * @returns {Promise<void>} Promise that resolves when log config is set
   */
  async setLogConfig(config) {
    // Wait for WASM module to be loaded (started in constructor)
    await this._wasmLoadPromise;
    const Module = this._getWasmModule();

    const result = Module.ccall(
      'SetLogConfig',
      'number',
      ['bool', 'bool', 'number', 'string'],
      [
        config.consoleEnabled || false,
        config.fileEnabled || false,
        config.level || 0,
        config.fileName || ''
      ]
    );

    checkResult(result, 'Failed to set log config');
  }

  /**
   * Enables or disables a beauty type
   * @param {number} beautyType - Beauty type (use BeautyType enum)
   * @param {boolean} enabled - Enable or disable
   */
  setBeautyTypeEnabled(beautyType, enabled) {
    this._ensureInitialized();
    const Module = this._getWasmModule();

    const result = Module.ccall(
      'SetBeautyTypeEnabled',
      'number',
      ['number', 'number', 'number'],
      [this.enginePtr, beautyType, enabled ? 1 : 0]
    );

    checkResult(result, `Failed to ${enabled ? 'enable' : 'disable'} beauty type`);
  }

  /**
   * Checks if a beauty type is enabled
   * @param {number} beautyType - Beauty type (use BeautyType enum)
   * @returns {boolean} True if enabled
   */
  isBeautyTypeEnabled(beautyType) {
    this._ensureInitialized();
    const Module = this._getWasmModule();

    const result = Module.ccall(
      'IsBeautyTypeEnabled',
      'number',
      ['number', 'number'],
      [this.enginePtr, beautyType]
    );

    if (result === -1) {
      throw new FacebetterError('Failed to check beauty type status');
    }

    return result === 1;
  }

  /**
   * Disables all beauty types
   */
  disableAllBeautyTypes() {
    this._ensureInitialized();
    const Module = this._getWasmModule();

    const result = Module.ccall(
      'DisableAllBeautyTypes',
      'number',
      ['number'],
      [this.enginePtr]
    );

    checkResult(result, 'Failed to disable all beauty types');
  }

  /**
   * Sets a basic beauty parameter
   * @param {number} param - Parameter (use BasicParam enum)
   * @param {number} value - Parameter value (0.0 - 1.0)
   */
  setBasicParam(param, value) {
    this._ensureInitialized();
    this._setBeautyParam('SetBeautyParamBasic', param, value);
  }

  /**
   * Sets a reshape parameter
   * @param {number} param - Parameter (use ReshapeParam enum)
   * @param {number} value - Parameter value (0.0 - 1.0)
   */
  setReshapeParam(param, value) {
    this._ensureInitialized();
    this._setBeautyParam('SetBeautyParamReshape', param, value);
  }

  /**
   * Sets a makeup parameter
   * @param {number} param - Parameter (use MakeupParam enum)
   * @param {number} value - Parameter value (0.0 - 1.0)
   */
  setMakeupParam(param, value) {
    this._ensureInitialized();
    this._setBeautyParam('SetBeautyParamMakeup', param, value);
  }

  /**
   * Internal method to set beauty parameters
   * @private
   */
  _setBeautyParam(functionName, param, value) {
    if (value < 0 || value > 1) {
      throw new FacebetterError('Parameter value must be between 0.0 and 1.0');
    }

    const Module = this._getWasmModule();
    const result = Module.ccall(
      functionName,
      'number',
      ['number', 'number', 'number'],
      [this.enginePtr, param, value]
    );

    checkResult(result, `Failed to set beauty parameter`);
  }

  /**
   * Sets virtual background options (unified API, matches C++/Java/OC)
   * @param {VirtualBackgroundOptions|Object} options - Virtual background options
   * @param {number} [options.mode] - Background mode (use BackgroundMode enum)
   * @param {ImageData|HTMLImageElement|HTMLCanvasElement} [options.backgroundImage] - Background image (required when mode is Image)
   */
  setVirtualBackground(options) {
    this._ensureInitialized();
    const Module = this._getWasmModule();
    
    // Support both VirtualBackgroundOptions object and plain object
    const opts = options.mode !== undefined ? options : { mode: BackgroundMode.None, backgroundImage: null };
    
    let imageData = null;
    let imageBufferPtr = null;
    
    try {
      // 如果提供了背景图片，转换为 ImageData 并准备缓冲区
      if (opts.backgroundImage) {
        imageData = this._toImageData(opts.backgroundImage);
        const bufferSize = imageData.width * imageData.height * 4;
        imageBufferPtr = Module._malloc(bufferSize);
        
        const buffer = this._getWasmBuffer();
        const view = new Uint8Array(buffer, imageBufferPtr, bufferSize);
        view.set(imageData.data);
      }
      
      // 使用统一的 SetVirtualBackground C 接口（与其他平台一致）
      const result = Module.ccall(
        'SetVirtualBackground',
        'number',
        ['number', 'number', 'number', 'number', 'number', 'number'],
        [
          this.enginePtr,
          opts.mode,
          imageBufferPtr || 0,  // 如果为 null，传递 0
          imageData ? imageData.width : 0,
          imageData ? imageData.height : 0,
          imageData ? imageData.width * 4 : 0
        ]
      );
      
      checkResult(result, 'Failed to set virtual background');
    } finally {
      // 释放内存
      if (imageBufferPtr) {
        Module._free(imageBufferPtr);
      }
    }
  }


  /**
   * Ensures buffers are allocated for the given dimensions
   * @private
   */
  _ensureBuffers(width, height) {
    const requiredSize = width * height * 4;

    if (this.bufferSize < requiredSize) {
      const Module = this._getWasmModule();

      if (this.srcBufferPtr) {
        Module._free(this.srcBufferPtr);
      }
      if (this.dstBufferPtr) {
        Module._free(this.dstBufferPtr);
      }

      this.srcBufferPtr = Module._malloc(requiredSize);
      this.dstBufferPtr = Module._malloc(requiredSize);
      this.bufferSize = requiredSize;

      if (!this.srcBufferPtr || !this.dstBufferPtr) {
        throw new FacebetterError('Failed to allocate memory buffers');
      }
    }
  }

  /**
   * Processes an image
   * @param {ImageData|HTMLImageElement|HTMLCanvasElement|HTMLVideoElement|Uint8ClampedArray} input - Input image
   * @param {number} width - Image width (required if input is Uint8ClampedArray)
   * @param {number} height - Image height (required if input is Uint8ClampedArray)
   * @param {number} processMode - Process mode (use ProcessMode enum, default: ProcessMode.Image)
   * @returns {ImageData} Processed image data
   */
  processImage(input, width, height, processMode = ProcessMode.Image) {
    this._ensureInitialized();
    const imageData = this._platformAPI.toImageData(input, width, height);
    const Module = this._getWasmModule();

    this._ensureBuffers(imageData.width, imageData.height);

    const bufferSize = imageData.width * imageData.height * 4;

    try {
      const buffer = this._getWasmBuffer();
      
      const srcView = new Uint8Array(buffer, this.srcBufferPtr, bufferSize);
      srcView.set(imageData.data);

      const result = Module.ccall(
        'ProcessImageRGBA',
        'number',
        ['number', 'number', 'number', 'number', 'number', 'number', 'number'],
        [
          this.enginePtr,
          this.srcBufferPtr,
          imageData.width,
          imageData.height,
          imageData.width * 4,
          this.dstBufferPtr,
          processMode
        ]
      );

      checkResult(result, 'Failed to process image');

      const currentBuffer = this._getWasmBuffer();
      const dstView = new Uint8Array(currentBuffer, this.dstBufferPtr, bufferSize);
      const processedData = new Uint8ClampedArray(dstView);

      return new ImageData(processedData, imageData.width, imageData.height);
    } catch (error) {
      if (error instanceof FacebetterError) {
        throw error;
      }
      throw new FacebetterError(`Image processing error: ${error.message}`);
    }
  }

  /**
   * Ensures the engine is initialized
   * @private
   * @throws {FacebetterError} If engine is not initialized
   */
  _ensureInitialized() {
    if (!this.initialized || !this.enginePtr) {
      throw new FacebetterError('Engine not initialized. Call init() first.');
    }
  }

  /**
   * Ensures gpupixel_canvas exists in the DOM (browser only)
   * This canvas is required by GPUPixel for WebGL context creation
   * @private
   */
  _ensureGPUPixelCanvas() {
    if (typeof document === 'undefined') {
      return;
    }
    
    let canvas = document.getElementById('gpupixel_canvas');
    if (!canvas) {
      canvas = document.createElement('canvas');
      canvas.id = 'gpupixel_canvas';
      canvas.style.display = 'none';
      canvas.width = 1;
      canvas.height = 1;
      document.body.appendChild(canvas);
      this._createdGPUPixelCanvas = true;
    }
    this._gpupixelCanvas = canvas;
  }

  /**
   * Cleans up gpupixel_canvas if it was created by this engine instance (browser only)
   * @private
   */
  _cleanupGPUPixelCanvas() {
    if (this._createdGPUPixelCanvas && this._gpupixelCanvas) {
      this._gpupixelCanvas.remove();
      this._gpupixelCanvas = null;
      this._createdGPUPixelCanvas = false;
    }
  }

  /**
   * Ensures offscreen canvas exists and matches the required dimensions (browser only)
   * This canvas is reused for video processing to avoid creating temporary canvases
   * @private
   * @param {number} width - Required canvas width
   * @param {number} height - Required canvas height
   */
  _ensureOffscreenCanvas(width, height) {
    if (typeof document === 'undefined') {
      return null;
    }
    
    if (!this._offscreenCanvas || 
        this._offscreenCanvas.width !== width || 
        this._offscreenCanvas.height !== height) {
      this._offscreenCanvas = document.createElement('canvas');
      this._offscreenCanvas.width = width;
      this._offscreenCanvas.height = height;
      this._offscreenCtx = this._offscreenCanvas.getContext('2d', { willReadFrequently: true });
    }
    return this._offscreenCanvas;
  }

}

